import { AttachFileOutlined, SimCardAlertOutlined } from "@mui/icons-material";
import { Box, Card, CircularProgress } from "@mui/material";
import { grey, red } from "@mui/material/colors";
import { isEmpty, isNil, size } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import AgGridSearchableTableWrapper from "../../../components/agGrid/AgGridSearchableTableWrapper";
import ExpandCollapseIconButton from "../../../components/buttons/ExpandCollapseIconButton";
import FileUploadFieldWithModal from "../../../components/common/FileUploadFieldWithModal";
import Flx from "../../../components/layout/Flx";
import ToggleTabSwitcher from "../../../components/navigation/ToggleTabSwitcher";
import BasicFilesTable from "../../../components/tables/BasicFilesTable";
import MissingFilesTable from "../../../components/tables/MissingFilesTable";
import Txt from "../../../components/typography/Txt";
import { useEndpointHook } from "../../../hooks/useEndpointHook";
import ProfileSection from "../components/ProfileSection";

const ProfileDocumentLibrary = ({ activeTab, keyName }) => {
  const profile = useSelector((state) => state?.profile);

  const borrowerDocuments = profile?.borrowerDocuments || [];
  const [isAdding, setIsAdding] = useState(false);
  const [rowGroup, setRowGroup] = useState("List View");

  const filesMappedWithRequirements = useMemo(() => {
    const createdMissingFileItem = ({ docGroup, docType }) => {
      return {
        _id: `missing-${docGroup}-${docType}`,
        docGroup,
        docType,
        file_display_name: `Missing Document`,
        // file_display_name: `No ${docType} uploaded`,
        isMissing: true, // Flag to identify placeholder items
      };
    };

    const requiredDocGroupsAndTypes = {
      "Borrower Documents": [
        "Borrower Experience - HUDs, Loan Docs",
        "Borrower Experience - Track Record",
        "Photo Identification",
      ],
      "Entity Docs": [
        "Articles of Organization",
        "COGS",
        "EIN",
        "Operating Agreement",
      ],
      Liquidity: ["Bank Statement", "Other Account Type Statement"],
      // "Property Documents": [
      //   "Purchase Contract",
      //   "Purchase Contract - Amendment",
      //   "Purchase Contract - Assignment of Contract",
      // ],
    };

    // Start with the actual files
    let baseFiles = [...(borrowerDocuments || [])];

    // Iterate through all required docGroups and docTypes
    Object.entries(requiredDocGroupsAndTypes).forEach(
      ([docGroup, docTypes]) => {
        docTypes.forEach((docType) => {
          // Check if this docGroup/docType combination exists in the files
          const existingFile = baseFiles.find(
            (file) => file.docGroup === docGroup && file.docType === docType,
          );

          // If it doesn't exist, create a missing file item
          if (!existingFile) {
            baseFiles.push(createdMissingFileItem({ docGroup, docType }));
          }
        });
      },
    );

    return baseFiles;
  }, [borrowerDocuments]);

  return (
    <ProfileSection
      activeTab={activeTab}
      title={"Document Library"}
      description={"Upload and manage documents in your profile."}
      icon={<AttachFileOutlined sx={{ mr: 0.3 }} className="thin" />}
      // endContent={
      //   <Button
      //     startIcon={<UploadFile className="thin" />}
      //     onClick={() => setIsAdding(true)}
      //   >
      //     Upload Document
      //   </Button>
      // }
      endContent={
        <Box
          sx={{
            border: `1px solid ${grey[300]}`,
            borderRadius: "8px",
            overflow: "hidden",
            ".MuiButtonBase-root": {
              fontSize: "11px !important",
            },
          }}
        >
          <ToggleTabSwitcher
            value={rowGroup}
            onChange={setRowGroup}
            tabs={["List View", "By Document Type", "By Category"]}
          />
        </Box>
      }
    >
      <ContentRenderer
        missingFiles={filesMappedWithRequirements}
        files={borrowerDocuments}
        rowGroup={rowGroup}
      />
    </ProfileSection>
  );
};

const ContentRenderer = ({ missingFiles, files, rowGroup }) => {
  const profile = useSelector((state) => state?.profile);

  if (isNil(profile)) {
    return (
      <Card sx={{ py: 3 }}>
        <Flx g={2} center fw column>
          <CircularProgress size={18} />
          <Txt secondary>Getting your profile information...</Txt>
        </Flx>
      </Card>
    );
  }

  return (
    <ProfileFileManager
      missingFiles={missingFiles}
      files={files}
      rowGroup={rowGroup}
    />
  );
};

const ProfileFileManager = ({ missingFiles, files, rowGroup }) => {
  const [uploadedFiles, setUploadedFiles] = useState(null);
  const { loading, uploadProfileDocuments } = useEndpointHook();
  const profile = useSelector((state) => state?.profile);

  const handleOnSubmit = (f) => {
    uploadProfileDocuments({
      files: f,

      onSuccessFn: () => {
        setUploadedFiles(null);
      },
    });
  };
  return (
    <Flx column g={1.5}>
      {/* <MissingProfileDocumentsTable files={files} rowGroup={rowGroup} /> */}
      <FileUploadFieldWithModal
        suppressUploadButton
        borderSize={1.5}
        minHeight={"88px"}
        keyName={`file-uploader-card-${profile}`}
        onSubmit={handleOnSubmit}
        loading={loading}
        // inputSx={{ background: grey[50] }}
        files={uploadedFiles}
        modalTitle={"Upload Profile Documents"}
        setFiles={setUploadedFiles}
      />
      <MissingProfileDocumentsTable files={missingFiles} rowGroup={rowGroup} />
      <SearchableDocumentsTable files={files} rowGroup={rowGroup} />
    </Flx>
  );
};

const MissingProfileDocumentsTable = ({ files, rowGroup }) => {
  const [show, setShow] = useState(false);
  const filteredMissing = useMemo(() => {
    return files.filter((f) => f.isMissing);
  }, [files]);

  if (isEmpty(filteredMissing)) {
    return null;
  }
  return (
    <Flx
      sx={{
        mt: 3,
        mb: 2,
        background: red[50],
        borderRadius: "8px",

        // gap: 1,
        cursor: "pointer",
        overflow: "hidden",
      }}
      column
      g={0}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          // p: 0.5,
          p: 2,
          pb: show ? 1 : 2,
          transition: "background 0.3s",

          ":hover": { background: red[100] },
        }}
        onClick={() => setShow(!show)}
      >
        <Flx g={0.5} ac>
          <SimCardAlertOutlined
            // className="thin"
            sx={{ fontSize: "38px", color: red[700] }}
          />
          <Flx column>
            <Txt bold sx={{ fontStyle: "italic", fontSize: "12px" }}>
              Missing Required Documents ({size(filteredMissing)})
            </Txt>
            <Txt
              // sx={{ fontSize: "11px" }}
              //
              secondary
            >
              The following required documents are missing from your profile.
              Please upload them as soon as possible to complete your profile.
            </Txt>
          </Flx>
        </Flx>

        <ExpandCollapseIconButton
          // onClick={() => setShow(!show)}
          active={show}
        />
      </Box>
      {show && (
        <Flx
          column
          sx={{
            p: 2,
            pt: 1,
          }}
        >
          <MissingFilesTable
            hideSearch
            headerHeight={50}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              overflow: "hidden",
            }}
            showFileOptions
            files={filteredMissing}
            rowGroup={ROW_GROUP_MAP[rowGroup]}
          />
        </Flx>
      )}
      {/* <Divider sx={{ mt: 5, mb: 4 }} /> */}
    </Flx>
  );
};

const SearchableDocumentsTable = ({
  hideSearch = false,
  rowGroup,
  files,
  quickFilter,
  setQuickFilter,
}) => {
  const [localQuickFilter, setLocalQuickFilter] = useState(null);
  const rowStyles = useCallback((params) => {
    if (params?.data?.isMissing) {
      return {
        background: red[50],
        fontStyle: "italic",
        fontWeight: 400,
      };
    }
  }, []);

  return (
    <AgGridSearchableTableWrapper
      quickFilter={quickFilter || localQuickFilter}
      setQuickFilter={setQuickFilter || setLocalQuickFilter}
      hideSearch={hideSearch}
    >
      <BasicFilesTable
        headerHeight={50}
        sort="uploadDateDesc"
        quickFilterText={quickFilter || localQuickFilter}
        showUploadDate={!hideSearch}
        rowStyles={rowStyles}
        hideFileCellDate
        showFileOptions
        showStatus={false}
        rowGroup={ROW_GROUP_MAP[rowGroup]}
        files={files}
      />
    </AgGridSearchableTableWrapper>
  );
};
const ROW_GROUP_MAP = {
  "List View": null,
  "Categorized View": "docGroup",
  "By Document Type": "docType",
  "By Status": "documentStatus",
  "By Category": "docGroup",
};
export default ProfileDocumentLibrary;
